import { RRule } from "rrule";
import * as Yup from "yup";
import { uniq } from "lodash";
import { MAX_TERMS_DAYS, ValidationMessage } from "./constants";

export const isValidRRuleString = value => {
  if (!value) {
    return false;
  }
  if (value.includes("NaN")) {
    return false;
  }
  try {
    RRule.fromString(value);
    return true;
  } catch {
    return false;
  }
};

const hasValue = (value, allowNegative = false) => {
  if (value == null) {
    return false;
  }
  if (typeof value === "string") {
    return value.length > 0;
  }
  if (Array.isArray(value)) {
    return value.filter(Boolean).length > 0;
  }
  if (typeof value === "number") {
    return allowNegative ? true : value >= 0;
  }
  if (typeof value === "object") {
    return true;
  }
  return false;
};

const validateRRuleConfig = config => {
  // If i have a frequency of yearly, then i must have a bymonthday set.
  // If i have a frequency of monthly, then i must have a bymonthday or byweekday set.
  // If i have a frequency of weekly, then i must have a byweekday set.
  // If i have a frequency of daily, then i must not have any bymonthday or byweekday set. But must have an interval set.
  if (!config || config.freq == null) return true; // Skip validation if no config or frequency

  const { freq, bymonthday, byweekday } = config;
  switch (freq) {
    case RRule.YEARLY:
      // Must have bymonthday
      return hasValue(bymonthday);

    case RRule.MONTHLY: {
      // Must have either bymonthday or byweekday (but not both)
      const hasByMonthDay = hasValue(bymonthday);
      const hasByWeekDay = hasValue(byweekday, true); // Allow -1 for byweekday (last weekday of the month)
      return hasByMonthDay !== hasByWeekDay;
    }

    case RRule.WEEKLY:
      // Must have byweekday
      return hasValue(byweekday);

    default:
      // Unknown frequency - allow it to pass validation
      return true;
  }
};

function rruleConfigYupTest() {
  return Yup.object()
    .shape({
      freq: Yup.number().nullable(true),
      interval: Yup.number().nullable(true),
      bymonthday: Yup.mixed()
        .nullable(true)
        .test(
          "bymonthday-validation",
          "Day must be in range",
          function (value) {
            let maxDay = 31;
            if (value === null || value === undefined) {
              return true;
            }
            if (this.parent?.freq === RRule.YEARLY) {
              // For yearly freq (which is quarterly), max day is 92
              maxDay = 92;
            }
            if (typeof value === "number") {
              return value >= 1 && value <= maxDay;
            }
            if (Array.isArray(value)) {
              if (value.length === 0) {
                return true;
              }
              return value.every(
                day => typeof day === "number" && day >= 1 && day <= maxDay,
              );
            }
            return false;
          },
        ),
      byweekday: Yup.mixed().nullable(true),
      count: Yup.number().nullable(true),
      bymonth: Yup.mixed().nullable(true),
    })
    .test(
      "frequency-rules",
      "Invalid configuration for the selected frequency",
      function (config) {
        const isValid = validateRRuleConfig(config);

        if (!isValid && config?.freq != null) {
          switch (config.freq) {
            case RRule.YEARLY:
              return this.createError({
                message: "Please choose a specific day within the quarter",
              });
            case RRule.MONTHLY:
              return this.createError({
                message:
                  "Please choose either a specific date (like the 15th) or a day of the week (like Monday), but not both.",
              });
            case RRule.WEEKLY:
              return this.createError({
                message:
                  "Please choose a day of the week (like Monday or Friday).",
              });
            default:
              return this.createError({
                message: "Unknown frequency type",
              });
          }
        }
        return isValid;
      },
    );
}

export const tradingTermValidation = Yup.array()
  .nullable()
  .of(
    Yup.object().shape({
      speciesId: Yup.number().nullable(true),
      saleType: Yup.string().nullable(true),
      saleSubTypeId: Yup.number().nullable(true),
      buyerScheduleDueRule: Yup.string().nullable(),
      buyerTerms: Yup.number()
        .required(ValidationMessage.REQUIRED)
        .max(MAX_TERMS_DAYS, ValidationMessage.MAX_TERMS),
      buyerConfig: rruleConfigYupTest(),
      vendorTerms: Yup.number()
        .required(ValidationMessage.REQUIRED)
        .max(MAX_TERMS_DAYS, ValidationMessage.MAX_TERMS),
      vendorScheduleDueRule: Yup.string().nullable(),
      vendorConfig: rruleConfigYupTest(),
    }),
  )
  .test(
    "non-dupe",
    ValidationMessage.NON_UNIQUE,
    value =>
      uniq(
        value?.map(
          tradingTerm =>
            `${tradingTerm.saleType}${tradingTerm.speciesId}${tradingTerm.saleSubTypeId}`,
        ),
      ).length === (value?.length || 0),
  );
